/**
 * useV2ConditionEditor
 *
 * React hook for the V2 condition editor.
 * Manages condition rows grouped into AND-groups combined with OR.
 *
 * Internal state: `V2ConditionRow[][]` (explicit groups).
 * Groups are the semantic unit — no `logic` field on V2ConditionRow needed.
 * Serialization to `RouteGuard[]` (with logic connectors) happens on save via joinGroups().
 *
 * Group management:
 * - handleLogicToggle(groupIdx, rowIdx) toggles the AND/OR connector AFTER row[rowIdx]:
 *     AND chip (same group) → toggles to OR → splits group at that point
 *     OR chip (different groups) → toggles to AND → merges groups N and N+1
 * - addCondition(groupIdx) adds empty row at the end of group N
 * - removeCondition(groupIdx, rowIdx) removes row; empty groups are dropped
 *
 * @module v2-condition-editor
 */

import { useState, useMemo, useCallback } from 'react';
import type {
  ComparisonOperator,
  RouteGuard,
  FlowDocument,
} from '@segmently/schema-models/v2';
import type {
  V2DataSource,
  V2ConditionRow,
  V2RuleGroup,
  V2TargetValueOptions,
  V2ConditionConflict,
} from '../types/v2-condition-editor.types';
import type { V2OperatorInfo } from '@modules/v2-variable-utils/types/variable-utils.types';
import { v2DataSourceService } from '../services/V2DataSourceService';
import { v2OperatorService } from '../services/V2OperatorService';
import { v2TargetValueProvider } from '../services/V2TargetValueProvider';
import { v2ConditionValidator } from '../services/V2ConditionValidator';
import { v2ConflictDetector } from '../services/V2ConflictDetector';
import { splitIntoGroups, joinGroups } from '../utils/conditionGrouping';

// ============================================================================
// HELPERS
// ============================================================================

let _nextId = 1;
function genRowId(): string {
  return `v2row_${_nextId++}`;
}
function genGroupId(): string {
  return `v2grp_${_nextId++}`;
}

function createEmptyRow(): V2ConditionRow {
  return { id: genRowId(), key: '', operator: '' as ComparisonOperator, value: '', isValid: false };
}

/** Convert RouteGuard → V2ConditionRow (initialisation only) */
function guardToRow(guard: RouteGuard, sources: V2DataSource[]): V2ConditionRow {
  const sourceVariable = sources.find((s) => s.variableId === guard.key);
  return {
    id: genRowId(),
    key: guard.key,
    operator: guard.operator || ('' as ComparisonOperator),
    value: guard.value ?? '',
    sourceVariable,
    isValid: Boolean(guard.key && guard.operator),
  };
}

/** Convert V2ConditionRow → RouteGuard (no logic — joinGroups assigns it) */
function rowToGuard(row: V2ConditionRow): RouteGuard {
  return {
    key: row.key,
    operator: row.operator as RouteGuard['operator'],
    value: row.value,
  };
}

// ============================================================================
// TYPES
// ============================================================================

export interface UseV2ConditionEditorParams {
  flowDocument: FlowDocument;
  sourceStepId: string;
  initialConditions?: RouteGuard[];
}

/** Logic connector between adjacent rows. Used by the UI to render AND/OR chips. */
export type RowConnectorLogic = 'and' | 'or' | null; // null = last row (no chip)

export interface UseV2ConditionEditorReturn {
  /** Rule groups computed from internal state. Each group = AND-conditions. */
  groups: V2RuleGroup[];
  /** Available data sources (variables) for the source step */
  dataSources: V2DataSource[];
  /** Logic connector AFTER row (groupIdx, rowIdx). null = last row. */
  getConnectorAfter(groupIdx: number, rowIdx: number): RowConnectorLogic;

  /** Get available operators for a data source */
  getOperators(dataSource: V2DataSource): V2OperatorInfo[];
  /** Get value input config for a data source + operator */
  getValueOptions(dataSource: V2DataSource, operator: ComparisonOperator | string): V2TargetValueOptions;

  /** Cascade: variable change resets operator + value */
  handleVariableChange(groupIdx: number, rowIdx: number, variableId: string): void;
  /** Cascade: operator change clears value for incompatible types */
  handleOperatorChange(groupIdx: number, rowIdx: number, operator: ComparisonOperator): void;
  handleValueChange(groupIdx: number, rowIdx: number, value: string): void;

  /**
   * Toggle the AND/OR connector AFTER row (groupIdx, rowIdx).
   * - If rowIdx < group.length-1: same group → split group at that point (AND→OR)
   * - If rowIdx == group.length-1 && next group exists: between groups → merge (OR→AND)
   */
  handleLogicToggle(groupIdx: number, rowIdx: number): void;

  /** Add empty row to the end of group N */
  addCondition(groupIdx: number): void;
  /** Remove row (groupIdx, rowIdx). Empty groups are removed. At least 1 empty row remains. */
  removeCondition(groupIdx: number, rowIdx: number): void;

  /** True if all rows with key+operator are valid */
  isValid: boolean;
  /** All detected conflicts (including redundant) */
  conflicts: V2ConditionConflict[];
  /** Blocking conflicts (mutual_exclusion, always_false) that prevent save */
  blockingConflicts: V2ConditionConflict[];

  /** Serialize to RouteGuard[] (with logic connectors) ready for storage */
  toRouteGuards(): RouteGuard[];
}

// ============================================================================
// HOOK
// ============================================================================

export function useV2ConditionEditor({
  flowDocument,
  sourceStepId,
  initialConditions,
}: UseV2ConditionEditorParams): UseV2ConditionEditorReturn {

  // Resolve available data sources once (stable for the lifetime of the dialog)
  const dataSources = useMemo(
    () => v2DataSourceService.getAvailableDataSources(flowDocument, sourceStepId),
    [flowDocument, sourceStepId],
  );

  // ── Internal state: groups of rows ────────────────────────────────────────
  type GroupState = { id: string; rows: V2ConditionRow[] };

  const [groupStates, setGroupStates] = useState<GroupState[]>(() => {
    if (!initialConditions || initialConditions.length === 0) {
      return [{ id: genGroupId(), rows: [createEmptyRow()] }];
    }
    const rawGroups = splitIntoGroups(initialConditions);
    return rawGroups.map((guards) => ({
      id: genGroupId(),
      rows: guards.map((g) => guardToRow(g, dataSources)),
    }));
  });

  // ── Exposed groups (V2RuleGroup[]) ─────────────────────────────────────────
  const groups = useMemo<V2RuleGroup[]>(
    () => groupStates.map((gs) => ({ id: gs.id, conditions: gs.rows })),
    [groupStates],
  );

  // ── Connector logic ────────────────────────────────────────────────────────
  const getConnectorAfter = useCallback(
    (groupIdx: number, rowIdx: number): RowConnectorLogic => {
      const group = groupStates[groupIdx];
      if (!group) return null;
      const isLastRowInGroup = rowIdx === group.rows.length - 1;
      const isLastGroup = groupIdx === groupStates.length - 1;
      if (isLastRowInGroup && isLastGroup) return null; // last row overall — no chip
      if (!isLastRowInGroup) return 'and';              // within group → AND
      return 'or';                                      // between groups → OR
    },
    [groupStates],
  );

  // ── Immutable state helpers ───────────────────────────────────────────────
  const updateRow = useCallback(
    (groupIdx: number, rowIdx: number, update: Partial<V2ConditionRow>) => {
      setGroupStates((prev) => {
        const next = prev.map((gs, gi) => {
          if (gi !== groupIdx) return gs;
          return {
            ...gs,
            rows: gs.rows.map((row, ri) =>
              ri === rowIdx ? { ...row, ...update } : row,
            ),
          };
        });
        return next;
      });
    },
    [],
  );

  // ── Change handlers ───────────────────────────────────────────────────────
  const handleVariableChange = useCallback(
    (groupIdx: number, rowIdx: number, variableId: string) => {
      const sourceVariable = dataSources.find((s) => s.variableId === variableId);
      updateRow(groupIdx, rowIdx, {
        key: variableId,
        operator: '' as ComparisonOperator,
        value: '',
        sourceVariable,
        isValid: false,
        validationError: undefined,
      });
    },
    [dataSources, updateRow],
  );

  const handleOperatorChange = useCallback(
    (groupIdx: number, rowIdx: number, operator: ComparisonOperator) => {
      const requiresValue = v2OperatorService.operatorRequiresValue(operator);
      updateRow(groupIdx, rowIdx, {
        operator,
        value: requiresValue ? '' : '',
        isValid: false,
        validationError: undefined,
      });
    },
    [updateRow],
  );

  const handleValueChange = useCallback(
    (groupIdx: number, rowIdx: number, value: string) => {
      updateRow(groupIdx, rowIdx, { value, validationError: undefined });
    },
    [updateRow],
  );

  // ── Logic toggle ──────────────────────────────────────────────────────────
  const handleLogicToggle = useCallback(
    (groupIdx: number, rowIdx: number) => {
      setGroupStates((prev) => {
        const group = prev[groupIdx];
        if (!group) return prev;

        const isLastInGroup = rowIdx === group.rows.length - 1;
        const isLastGroup = groupIdx === prev.length - 1;

        if (!isLastInGroup) {
          // Within group → AND chip → split into two groups
          const before = group.rows.slice(0, rowIdx + 1);
          const after = group.rows.slice(rowIdx + 1);
          const next = [...prev];
          next.splice(groupIdx, 1,
            { id: group.id, rows: before },
            { id: genGroupId(), rows: after },
          );
          return next;
        } else if (!isLastGroup) {
          // Between groups → OR chip → merge with next group
          const nextGroup = prev[groupIdx + 1];
          const merged: GroupState = {
            id: group.id,
            rows: [...group.rows, ...nextGroup.rows],
          };
          const next = [...prev];
          next.splice(groupIdx, 2, merged);
          return next;
        }

        return prev; // last row of last group — nothing to toggle
      });
    },
    [],
  );

  // ── Add / Remove conditions ───────────────────────────────────────────────
  const addCondition = useCallback(
    (groupIdx: number) => {
      setGroupStates((prev) => {
        const group = prev[groupIdx];
        if (!group) return prev;
        return prev.map((gs, gi) =>
          gi === groupIdx
            ? { ...gs, rows: [...gs.rows, createEmptyRow()] }
            : gs,
        );
      });
    },
    [],
  );

  const removeCondition = useCallback(
    (groupIdx: number, rowIdx: number) => {
      setGroupStates((prev) => {
        const group = prev[groupIdx];
        if (!group) return prev;

        const newRows = group.rows.filter((_, ri) => ri !== rowIdx);
        let next: GroupState[];

        if (newRows.length === 0) {
          // Drop empty group
          next = prev.filter((_, gi) => gi !== groupIdx);
          // Ensure at least one group with one row
          if (next.length === 0) {
            next = [{ id: genGroupId(), rows: [createEmptyRow()] }];
          }
        } else {
          next = prev.map((gs, gi) =>
            gi === groupIdx ? { ...gs, rows: newRows } : gs,
          );
        }

        return next;
      });
    },
    [],
  );

  // ── Validation & conflicts ────────────────────────────────────────────────
  const allRows = useMemo(
    () => groupStates.flatMap((gs) => gs.rows),
    [groupStates],
  );

  const validatedRows = useMemo(() => {
    return allRows.map((row) => {
      const result = v2ConditionValidator.validateRow(row, dataSources);
      return { ...row, isValid: result.isValid, validationError: result.error };
    });
  }, [allRows, dataSources]);

  const isValid = useMemo(
    () => validatedRows.every((r) => !r.key || r.isValid),
    [validatedRows],
  );

  // Detect conflicts WITHIN each AND-group only (not across OR-groups).
  // Conditions in different OR-groups are alternatives — mutually exclusive by design.
  const conflicts = useMemo(
    () => groupStates.flatMap((gs) =>
      v2ConflictDetector.detectConflicts(gs.rows),
    ),
    [groupStates],
  );

  const blockingConflicts = useMemo(
    () => conflicts.filter((c) => c.type === 'mutual_exclusion' || c.type === 'always_false'),
    [conflicts],
  );

  // ── Serialization ─────────────────────────────────────────────────────────
  const toRouteGuards = useCallback((): RouteGuard[] => {
    const validGroups = groupStates
      .map((gs) => gs.rows.filter((r) => r.key && r.operator).map(rowToGuard))
      .filter((g) => g.length > 0);
    return joinGroups(validGroups);
  }, [groupStates]);

  // ── Service delegates ─────────────────────────────────────────────────────
  const getOperators = useCallback(
    (dataSource: V2DataSource): V2OperatorInfo[] =>
      v2OperatorService.getOperatorsForDataSource(dataSource),
    [],
  );

  const getValueOptions = useCallback(
    (dataSource: V2DataSource, operator: ComparisonOperator | string): V2TargetValueOptions =>
      v2TargetValueProvider.getValueOptions(dataSource, operator),
    [],
  );

  return {
    groups,
    dataSources,
    getConnectorAfter,
    getOperators,
    getValueOptions,
    handleVariableChange,
    handleOperatorChange,
    handleValueChange,
    handleLogicToggle,
    addCondition,
    removeCondition,
    isValid,
    conflicts,
    blockingConflicts,
    toRouteGuards,
  };
}
